<script lang="ts" setup>
    import '../../../css/choice.css';
    import { onMounted, ref, reactive, computed } from 'vue';
    import { initPinia } from '../../lib/PiniaMgr';
    import { usePrivilegesStore } from '../../lib/Stores/PrivilegesStore' 
    import { LayoutOrientation, IconTypes, DisplayStyle  } from "../../Enums/UiEnums";
    import { DatabaseIdTypes, AppScreens } from "../../Enums/ChoiceEnums";
    import { IQuickPayTabItemEx, QuickPayTabType, QuickPayTabSetup } from '../../lib/Models/ScheduleModel';
    import { IQuickPayTabNotificationResult } from '../../lib/Models/QuickPayModel';
    import { ModalStyles, popAlert } from "../../lib/Notification";
    import ResizeMonitor from '../../components/ResizeMonitor.vue'
    import FoldingPanel from '../../components/FoldingPanel.vue';
    import PrintFaxQueue from '../../ChoiceScreens/Screens/PrintFaxQueue.vue';
    import QuickPayDemographics from '../../components/Schedule/QuickPayDemographics.vue';
    import QuickPayID from '../../components/Schedule/QuickPayID.vue';
    import QuickPayReceipt from '../../components/Schedule/QuickPayReceipt.vue';
    import QuickPaySuperbill from '../../components/Schedule/QuickPaySuperbill.vue';
    import QuickPayLetters from '../../components/Schedule/QuickPayLetters.vue';
    import QuickPayRxHistory from '../../components/Schedule/QuickPayRxHistory.vue';
    import Tab from "../../components/Tab.vue"
    import Icon from "../../components/Icon.vue"
    import ScreenNoPrivilege from "../../components/ScreenNoPrivilege.vue"
    import * as ChoiceApi from '../../lib/ChoiceApi';

    const scheduleAlertTypeId = 0;
    const windowHeight = ref<number>();
    const privilegesStore = usePrivilegesStore(initPinia());

    const emit = defineEmits(["optOutMML","refreshBalance"]);

    defineExpose({refreshTabNotification});

    const props = defineProps<{
        patientId: number,
        patientEid: number,
        appointmentId?: number,
        scheduleDate?: string,
        appointmentStatusId?: number,
        tabId: number,
        resizeOffset?: number,
        receiptId?: number
    }>();

    const formData = reactive<{
        quickPayTabs: IQuickPayTabItemEx[],
        tabId?: QuickPayTabSetup,
        showSetup: boolean,
        tabType: QuickPayTabType,
        patientName?: string,
        providerEid?: number,
        scheduleDate?: string,
        securityUsersId?: number,
        readPatientEligibility: boolean,
        readAuthorization: boolean,
        readIntakeAssignment: boolean,
        readEmrOrderSearch: boolean,
        readReceipt: boolean,
        readEmrSuperbill: boolean,
        readEmrLetters: boolean,
        readEmrRx: boolean,
        tabNotification?: IQuickPayTabNotificationResult,
        privilegesExist: boolean,
        useOldIntakeAssign: boolean
    }>({
        quickPayTabs: [],
        showSetup: false,
        tabType: QuickPayTabType.Dropdown,
        readPatientEligibility: false,
        readAuthorization: false,
        readIntakeAssignment: false,
        readEmrOrderSearch: false,
        readReceipt: false,
        readEmrSuperbill: false,
        readEmrLetters: false,
        readEmrRx: false,
        privilegesExist: false,
        useOldIntakeAssign: true
    });

    async function getPrivileges() {
        await privilegesStore.getScreens([AppScreens.PatientEligibility, AppScreens.Authorization, AppScreens.IntakeAssignment, AppScreens.EMROrderSearch, 
            AppScreens.Receipt, AppScreens.EMRSuperbill, AppScreens.EMRLetters, AppScreens.EMRRx]);
        formData.readPatientEligibility = privilegesStore.screensWithReadPrivileges[AppScreens.PatientEligibility];
        formData.readAuthorization = privilegesStore.screensWithReadPrivileges[AppScreens.Authorization];
        formData.readIntakeAssignment = privilegesStore.screensWithReadPrivileges[AppScreens.IntakeAssignment];
        formData.readEmrOrderSearch = privilegesStore.screensWithReadPrivileges[AppScreens.EMROrderSearch];
        formData.readReceipt = privilegesStore.screensWithReadPrivileges[AppScreens.Receipt];
        formData.readEmrSuperbill = privilegesStore.screensWithReadPrivileges[AppScreens.EMRSuperbill];
        formData.readEmrLetters = privilegesStore.screensWithReadPrivileges[AppScreens.EMRLetters];
        formData.readEmrRx = privilegesStore.screensWithReadPrivileges[AppScreens.EMRRx];
        formData.privilegesExist = true;
    }
    

    async function refresh() {
        const promiseUseOldIntakeCode = await ChoiceApi.user.useScreen('IntakeAssign');
        if(promiseUseOldIntakeCode){
           formData.useOldIntakeAssign = promiseUseOldIntakeCode.use;
        }
        const promisePrivileges = getPrivileges();
        const promiseTabList = refreshTabList();
        const promisePatient = refreshPatient();
        const promiseTabNotification = refreshTabNotification();
        await Promise.all([promisePrivileges, promiseTabList, promisePatient, promiseTabNotification]);
    }
    refresh();

    onMounted(async () => {
        await getAlerts();
        windowResize();
        formData.isMounted = true;
    });

    const patientSelected = computed<boolean>(() => props.patientId > 0);
    const apptUrl = computed<string>(() => props.appointmentId ? `&ApptID=${props.appointmentId}` : "");
    const eligibilityUrl = computed<string>(() => `/officemd/screens/patienteligibility.asp?EID=${props.patientEid}${apptUrl.value}`);
    const authorizationAppt = computed<string>(() => props.appointmentId ? `&appointment_id=${props.appointmentId}` : "");
    const authorizationUrl = computed<string>(() => `/officemd/screens/authorizationSelect.asp?assign=Y&patient_entity_id=${props.patientEid}${authorizationAppt.value}&location=patient_portal`);
    const intakeUrl = computed<string>(() => {
        if(formData.useOldIntakeAssign){
            return `/officemd/screens/IntakeAssign.htm?PID=${props.patientId}`;
        }
        return `/officemd/app/intake/IntakeAssign/${props.patientId}`;
    });
    const intakeClass = computed<string>(()=>{
        if(formData.useOldIntakeAssign){
            return `old-intake mt-2`;
        }
        return `intake mt-2`; 
    });
    const ordersUrl = computed<string>(() => `/officemd/screens/EMROrderSearch.asp?Launcher=ReceiptEntry&PatientId=${props.patientId}&fixed=Y`);

    function windowResize() {
        windowHeight.value = window.innerHeight - (props.resizeOffset ?? 140);
    }

    async function getAlerts() {
        if (props.patientId > 0) {
            const alerts = await ChoiceApi.patient.alerts(DatabaseIdTypes.patient_id, props.patientId, props.appointmentStatusId ?? scheduleAlertTypeId);
            if (alerts.length > 0) {
                const messageItems = alerts.map(item => `<li><b>${item.src}</b><br>${item.msg}</li>`);
                popAlert(`<ol>${messageItems.join("")}</ol>`, "Scheduling Alert", ModalStyles.info);
            }
        }
    }

    async function refreshTabList() {
        const result = await ChoiceApi.schedule.QuickPayTabList(props.tabId);
        formData.tabType = result.tabType;
        formData.quickPayTabs = result.tabList.filter(o => o.enabled) ?? [];
        const defaultTabIndex = formData.quickPayTabs.findIndex(o => o.expanded && o.enabled);
        if (defaultTabIndex >= 0) {
            formData.tabId = formData.quickPayTabs[defaultTabIndex].quickPayTabId;
            if (patientSelected.value)
                formData.quickPayTabs[defaultTabIndex].selectedOnce = true;
        }
        else {
            formData.tabId = undefined;
        }
    }

    async function refreshPatient() {
        if (!props.patientId)
            return;
        const patient = await ChoiceApi.quickPay.Patient(props.patientId, props.appointmentId);
        formData.patientName = patient?.patientName;
        formData.providerEid = patient?.providerEid;
        formData.scheduleDate = patient?.scheduleDate;
        formData.securityUsersId = patient?.securityUsersId;
    }

    async function refreshTabNotification() {
        if (!props.patientId)
            return;
        formData.tabNotification = await ChoiceApi.quickPay.TabNotification(props.patientId, props.appointmentId);
    }

    function onChangeTab(tabId : any) {
        const tabIndex = formData.quickPayTabs.findIndex(item => item.quickPayTabId == tabId);
        if (tabIndex < 0)
            return;
        if (!patientSelected.value)
            return;
        formData.quickPayTabs[tabIndex].selectedOnce = true;
    }

    function isNotificationTab(tab: IQuickPayTabItemEx) {
        if (tab.quickPayTabId == QuickPayTabSetup.Letters && formData.tabNotification?.lettersExist)
            return true;
        else if (tab.quickPayTabId == QuickPayTabSetup.Orders && formData.tabNotification?.ordersExist)
            return true;
        else if (tab.quickPayTabId == QuickPayTabSetup.Prescription && formData.tabNotification?.newRxExist)
            return true;
        else if (tab.quickPayTabId == QuickPayTabSetup.PrintQueue && formData.tabNotification?.printExist)
            return true;
        else if (tab.quickPayTabId == QuickPayTabSetup.Receipt && formData.tabNotification?.receiptsExist)
            return true;
        else if (tab.quickPayTabId == QuickPayTabSetup.Authorizations && formData.tabNotification?.authExist)
            return true;
        else if (tab.quickPayTabId == QuickPayTabSetup.Intakes && formData.tabNotification?.intakeExist)
            return true;
        else if (tab.quickPayTabId == QuickPayTabSetup.PatientDemographicsSummary && formData.tabNotification?.demoExist)
            return true;
        return false;
    }

    function isCompletedTab(tab: IQuickPayTabItemEx) {
        if (tab.quickPayTabId == QuickPayTabSetup.Claim && formData.tabNotification?.claimExist)
            return true;
        if (tab.quickPayTabId == QuickPayTabSetup.PatientDemographicsSummary && !formData.tabNotification?.demoExist)
            return true;
        return false;
    }

    function onOptOutMML() {
        emit('optOutMML');
    }

    function onRefreshBalance() {
        emit('refreshBalance');
    }
</script>

<style scoped>
    .printQueue {
        height: 500px;
    }
    .eligibility {
        height: 500px;
    }
    .authorization {
        height: 600px;
    }
    .old-intake {
        height: 380px;
    }
    .intake {
        height: 600px;
    }
    .orders {
        height: 650px;
    }
    .letters {
        height: 650px;
    }
    .superbill {
        height: 650px;
    }
</style>

<template>
    <ResizeMonitor @resize="windowResize" />
    <div v-if="formData.tabType == QuickPayTabType.Dropdown && formData.privilegesExist" :style="{height: windowHeight + 'px', overflowY: 'auto', overflowX: 'hidden'}">
        <template v-for="item in formData.quickPayTabs" >
            <div class="row">
                <div class="col">
                    <FoldingPanel :panel-open="item.expanded">
                        <template #title>
                            {{item.title}}
                        </template>
                        <template v-slot:marker v-if="isCompletedTab(item) && patientSelected">
                            <Icon 
                                :icon="IconTypes.CheckMark" 
                                :displayStyle="DisplayStyle.Light" 
                                class="text-isalus-primary ms-2 fw-bold"
                                title="There are completed items"/>                            
                        </template>
                        <template v-slot:marker v-if="isNotificationTab(item) && patientSelected">
                            <Icon 
                                :icon="IconTypes.Warning" 
                                :displayStyle="DisplayStyle.Warning" 
                                class="text-isalus-primary ms-2"
                                title="Items need attention"/>                            
                        </template>
                        <template #body>
                            <template v-if="patientSelected">
                                <div class="printQueue" v-if="item.quickPayTabId == QuickPayTabSetup.PrintQueue">
                                    <PrintFaxQueue
                                        :patientId="props.patientId"
                                        :patientEid="props.patientEid"
                                        :height="600"
                                        @optOutMML="onOptOutMML"/>
                                </div>
                                <div v-if="item.quickPayTabId == QuickPayTabSetup.PatientDemographicsSummary">
                                    <QuickPayDemographics
                                        :patientId="props.patientId"
                                        :patientEid="props.patientEid"
                                        :appointmentId="props.appointmentId"
                                        :scheduleDate="props.scheduleDate"
                                        :tabId="props.tabId"
                                        :validationExists="formData.tabNotification?.demoExist"
                                        :validationMessage="formData.tabNotification?.demoMessage" />
                                </div>
                                <div v-if="item.quickPayTabId == QuickPayTabSetup.PatientPhotographInsuranceCards">
                                    <QuickPayID
                                        :patientId="props.patientId"
                                        :patientEid="props.patientEid"
                                        :patientName="formData.patientName"
                                        :appointmentId="props.appointmentId"
                                        :scheduleDate="props.scheduleDate"
                                        :tabId="props.tabId"/>
                                </div>
                                <div v-if="item.quickPayTabId == QuickPayTabSetup.Receipt">
                                    <template v-if="formData.readReceipt">
                                        <QuickPayReceipt
                                            :patientId="props.patientId"
                                            :patientEid="props.patientEid"
                                            :appointmentId="props.appointmentId"
                                            :scheduleDate="props.scheduleDate"
                                            :tabId="props.tabId"
                                            :receiptId="props.receiptId"
                                            @refreshBalance="onRefreshBalance"/>
                                    </template>
                                    <template v-else>
                                        <ScreenNoPrivilege :screen="AppScreens.Receipt" />                                            
                                    </template>
                                </div>
                                <div class="eligibility mt-2" v-if="item.quickPayTabId == QuickPayTabSetup.Eligibility">
                                    <template v-if="formData.readPatientEligibility">
                                        <iframe :src="eligibilityUrl" title="Eligibility" class="border-isalus-primary" :style="{'overflow-y': 'hidden', 'overflow-x': 'hidden', 'width': '100%', 'height': '100%'}"></iframe>
                                    </template>
                                    <template v-else>
                                        <ScreenNoPrivilege :screen="AppScreens.PatientEligibility" />                                            
                                    </template>
                                </div>
                                <div class="authorization mt-2" v-if="item.quickPayTabId == QuickPayTabSetup.Authorizations">
                                    <template v-if="formData.readAuthorization">
                                        <iframe :src="authorizationUrl" title="Authorization Select" :style="{'overflow-y': 'hidden', 'overflow-x': 'hidden', 'width': '100%', 'height': '100%'}"></iframe>
                                    </template>
                                    <template v-else>
                                        <ScreenNoPrivilege :screen="AppScreens.Authorization" />                                            
                                    </template>
                                </div>
                                <div :class="intakeClass" v-if="item.quickPayTabId == QuickPayTabSetup.Intakes">
                                    <template v-if="formData.readIntakeAssignment">
                                            <iframe :src="intakeUrl" title="Intake Assignment" :style="{'overflow-y': 'hidden', 'overflow-x': 'hidden', 'width': '100%', 'height': '100%'}"></iframe>
                                    </template>
                                    <template v-else>
                                        <ScreenNoPrivilege :screen="AppScreens.IntakeAssignment" />                                            
                                    </template>
                                </div>
                                <div class="orders mt-2" v-if="item.quickPayTabId == QuickPayTabSetup.Orders">
                                    <template v-if="formData.readEmrOrderSearch">
                                        <iframe :src="ordersUrl" title="Open Orders" class="border-isalus-primary" :style="{'overflow-y': 'hidden', 'overflow-x': 'hidden', 'width': '100%', 'height': '100%'}"></iframe>
                                    </template>
                                    <template v-else>
                                        <ScreenNoPrivilege :screen="AppScreens.EMROrderSearch" />                                            
                                    </template>
                                </div>
                                <div class="superbill mt-2" v-if="item.quickPayTabId == QuickPayTabSetup.Claim">
                                    <template v-if="formData.readEmrSuperbill">
                                        <QuickPaySuperbill
                                            :patientId="props.patientId"
                                            :patientEid="props.patientEid"
                                            :appointmentId="props.appointmentId"
                                            :scheduleDate="formData.scheduleDate"
                                            :providerEid="formData.providerEid"
                                            :tabId="props.tabId"/>
                                    </template>
                                    <template v-else>
                                        <ScreenNoPrivilege :screen="AppScreens.EMRSuperbill" />                                            
                                    </template>
                                </div>
                                <div class="letters mt-2" v-if="item.quickPayTabId == QuickPayTabSetup.Letters">
                                    <template v-if="formData.readEmrLetters">
                                        <QuickPayLetters
                                            :patientId="props.patientId"
                                            :patientEid="props.patientEid"
                                            :appointmentId="props.appointmentId"
                                            :scheduleDate="formData.scheduleDate"
                                            :providerEid="formData.providerEid"
                                            :securityUsersId="formData.securityUsersId"
                                            :tabId="props.tabId"/>
                                    </template>
                                    <template v-else>
                                        <ScreenNoPrivilege :screen="AppScreens.EMRLetters" />                                            
                                    </template>
                                </div>
                                <div v-if="item.quickPayTabId == QuickPayTabSetup.Prescription">
                                    <template v-if="formData.readEmrRx">
                                        <QuickPayRxHistory
                                            :patientId="props.patientId"/>    
                                    </template>                            
                                    <template v-else>
                                        <ScreenNoPrivilege :screen="AppScreens.EMRRx" />                                            
                                    </template>
                                </div>
                                <div v-else>
                                </div>
                            </template>
                            <template v-if="!patientSelected">
                                No Patient Selected
                            </template>
                        </template>
                    </FoldingPanel>
                </div>
            </div>
        </template>
    </div>
    <div v-if="formData.tabType == QuickPayTabType.Tab  && formData.privilegesExist">
        <div class="row">
            <div class="col">
                <template v-for="item in formData.quickPayTabs">
                    <Tab 
                        :value="item.quickPayTabId" 
                        v-model="formData.tabId" 
                        :layout="LayoutOrientation.Horz"
                        @onChange="onChangeTab">
                        {{ item.label }}
                        <template v-slot:marker v-if="isCompletedTab(item) && patientSelected">
                            <Icon 
                                :icon="IconTypes.CheckMark" 
                                :displayStyle="DisplayStyle.Success" 
                                class="text-isalus-primary fw-bold"
                                title="There are completed items."/>                            
                        </template>
                        <template v-slot:marker v-if="isNotificationTab(item) && patientSelected">
                            <Icon 
                                :icon="IconTypes.Warning" 
                                :displayStyle="DisplayStyle.Warning" 
                                class="text-isalus-primary"
                                title="Items need attention"/>                            
                        </template>
                    </Tab>
                </template>        
            </div>
        </div>
        <div :style="{height: windowHeight + 'px', overflowY: 'auto', overflowX: 'hidden'}">
            <div class="row">
                <div class="col">
                    <template v-if="patientSelected">
                        <template v-for="item in formData.quickPayTabs">
                            <div class="row">
                                <div class="col ms-1">
                                    <div class="printQueue" v-if="item.quickPayTabId == QuickPayTabSetup.PrintQueue && item.selectedOnce" v-show="item.quickPayTabId == formData.tabId">
                                        <PrintFaxQueue
                                            :patientId="props.patientId"
                                            :patientEid="props.patientEid"
                                            @optOutMML="onOptOutMML"/>
                                    </div>
                                    <div class="mt-2" v-if="item.quickPayTabId == QuickPayTabSetup.PatientDemographicsSummary && item.selectedOnce" v-show="item.quickPayTabId == formData.tabId">
                                        <QuickPayDemographics
                                            :patientId="props.patientId"
                                            :patientEid="props.patientEid"
                                            :appointmentId="props.appointmentId"
                                            :scheduleDate="props.scheduleDate"
                                            :tabId="props.tabId"
                                            :validationExists="formData.tabNotification?.demoExist"
                                            :validationMessage="formData.tabNotification?.demoMessage" />
                                    </div>
                                    <div v-if="item.quickPayTabId == QuickPayTabSetup.PatientPhotographInsuranceCards && item.selectedOnce" v-show="item.quickPayTabId == formData.tabId">
                                        <QuickPayID
                                            :patientId="props.patientId"
                                            :patientEid="props.patientEid"
                                            :patientName="formData.patientName"
                                            :appointmentId="props.appointmentId"
                                            :scheduleDate="props.scheduleDate"
                                            :tabId="props.tabId"/>
                                    </div>
                                    <div v-if="item.quickPayTabId == QuickPayTabSetup.Receipt && item.selectedOnce" v-show="item.quickPayTabId == formData.tabId">
                                        <template v-if="formData.readReceipt">
                                            <QuickPayReceipt
                                                :patientId="props.patientId"
                                                :patientEid="props.patientEid"
                                                :appointmentId="props.appointmentId"
                                                :scheduleDate="props.scheduleDate"
                                                :tabId="props.tabId"
                                                :receiptId="props.receiptId"
                                                @refreshBalance="onRefreshBalance"/>
                                        </template>
                                        <template v-else>
                                            <ScreenNoPrivilege :screen="AppScreens.Receipt" />                                            
                                        </template>
                                    </div>
                                    <div class="eligibility mt-2" v-if="item.quickPayTabId == QuickPayTabSetup.Eligibility && item.selectedOnce" v-show="item.quickPayTabId == formData.tabId">
                                        <template v-if="formData.readPatientEligibility">
                                            <iframe :src="eligibilityUrl" title="Eligibility" class="border-isalus-primary" :style="{'overflow-y': 'hidden', 'overflow-x': 'hidden', 'width': '100%', 'height': '100%'}"></iframe>
                                        </template>
                                        <template v-else>
                                            <ScreenNoPrivilege :screen="AppScreens.PatientEligibility" />                                            
                                        </template>
                                    </div>
                                    <div class="authorization mt-2" v-if="item.quickPayTabId == QuickPayTabSetup.Authorizations && item.selectedOnce" v-show="item.quickPayTabId == formData.tabId">
                                        <template v-if="formData.readAuthorization">
                                            <iframe :src="authorizationUrl" title="Authorization Select" :style="{'overflow-y': 'hidden', 'overflow-x': 'hidden', 'width': '100%', 'height': '100%'}"></iframe>
                                        </template>
                                        <template v-else>
                                            <ScreenNoPrivilege :screen="AppScreens.Authorization" />                                            
                                        </template>
                                    </div>
                                    <div :class="intakeClass" v-if="item.quickPayTabId == QuickPayTabSetup.Intakes && item.selectedOnce" v-show="item.quickPayTabId == formData.tabId">
                                        <template v-if="formData.readIntakeAssignment">
                                            <iframe :src="intakeUrl" title="Intake Assignment" :style="{'overflow-y': 'hidden', 'overflow-x': 'hidden', 'width': '100%', 'height': '100%'}"></iframe>
                                        </template>
                                        <template v-else>
                                            <ScreenNoPrivilege :screen="AppScreens.IntakeAssignment" />                                            
                                        </template>
                                    </div>
                                    <div class="orders mt-2" v-if="item.quickPayTabId == QuickPayTabSetup.Orders && item.selectedOnce" v-show="item.quickPayTabId == formData.tabId">
                                        <template v-if="formData.readEmrOrderSearch">
                                            <iframe :src="ordersUrl" title="Open Orders" class="border-isalus-primary" :style="{'overflow-y': 'hidden', 'overflow-x': 'hidden', 'width': '100%', 'height': '100%'}"></iframe>
                                        </template>
                                        <template v-else>
                                            <ScreenNoPrivilege :screen="AppScreens.EMROrderSearch" />                                            
                                        </template>
                                    </div>
                                    <div v-if="item.quickPayTabId == QuickPayTabSetup.Claim && item.selectedOnce" v-show="item.quickPayTabId == formData.tabId">
                                        <template v-if="formData.readEmrSuperbill">
                                            <QuickPaySuperbill
                                                :patientId="props.patientId"
                                                :patientEid="props.patientEid"
                                                :appointmentId="props.appointmentId"
                                                :scheduleDate="formData.scheduleDate"
                                                :providerEid="formData.providerEid"
                                                :tabId="props.tabId"/>
                                        </template>
                                        <template v-else>
                                            <ScreenNoPrivilege :screen="AppScreens.EMRSuperbill" />                                            
                                        </template>
                                    </div>
                                    <div v-if="item.quickPayTabId == QuickPayTabSetup.Letters && item.selectedOnce" v-show="item.quickPayTabId == formData.tabId">
                                        <template v-if="formData.readEmrLetters">
                                            <QuickPayLetters
                                                :patientId="props.patientId"
                                                :patientEid="props.patientEid"
                                                :appointmentId="props.appointmentId"
                                                :scheduleDate="formData.scheduleDate"
                                                :providerEid="formData.providerEid"
                                                :securityUsersId="formData.securityUsersId"
                                                :tabId="props.tabId"/>
                                        </template>
                                        <template v-else>
                                            <ScreenNoPrivilege :screen="AppScreens.EMRLetters" />                                            
                                        </template>
                                    </div>
                                    <div v-if="item.quickPayTabId == QuickPayTabSetup.Prescription && item.selectedOnce" v-show="item.quickPayTabId == formData.tabId">
                                        <template v-if="formData.readEmrRx">
                                            <QuickPayRxHistory
                                                :patientId="props.patientId"/>
                                        </template>
                                        <template v-else>
                                            <ScreenNoPrivilege :screen="AppScreens.EMRRx" />                                            
                                        </template>
                                    </div>
                                    <div v-else>
                                    </div>
                                </div>
                            </div>
                        </template>
                    </template>
                </div>
            </div>
        </div>
    </div>
</template>

